import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { UpdateSubscriptionMutation } from "core/mutations";
import { withSnackbar } from "notistack";
import { useViewer } from "core/components/ViewerProvider";
import { useArena } from "core/components/ArenaProvider";
import classNames from "classnames";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsIcon from "@mui/icons-material/Notifications";

const PREFIX = "SubscribeContentButton";

const classes = {
  root: `${PREFIX}-root`,
  button: `${PREFIX}-button`,
  iconButton: `${PREFIX}-iconButton`,
  iconButtonActive: `${PREFIX}-iconButtonActive`,
  active: `${PREFIX}-active`,
};

const Root = styled("div")(({ theme }) => {
  return {
    [`& .${classes.button}`]: {
      whiteSpace: "nowrap",
    },

    [`& .${classes.iconButton}`]: {
      // Styling can be added here if needed
    },

    [`& .${classes.iconButtonActive}`]: {
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
      },
      "&:focus": {
        backgroundColor: theme.palette.primary.main,
      },
      "&:active": {
        backgroundColor: theme.palette.primary.main,
      },
    },

    [`& .${classes.active}`]: {
      // Styling can be added here if needed
    },
  };
});

const CONTENT = "CONTENT";

function SubscribeContentButton({
  enqueueSnackbar,
  content,
  className,
  size,
  fullWidth,
}) {
  const { t } = useTranslation("core");
  const viewer = useViewer();
  const arena = useArena();

  // Скрываем кнопку подписки на уведомления, если на арене отключена авторизация
  if (!arena?.isRegistrationEnabled) {
    return null;
  }

  const [updateSubscription, { loading, data }] = useMutation(
    UpdateSubscriptionMutation,
    {
      onError(error) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      },
    }
  );

  const contentId = content.id;

  const viewerSubscription =
    data?.updateSubscription || content?.viewerSubscription;

  const active = viewerSubscription?.active;

  const onClickSubscribeButton = (event) => {
    event.preventDefault();

    if (viewer) {
      return updateSubscription({
        variables: {
          input: {
            type: CONTENT,
            contentId,
            active: active ? false : true,
            notify: active ? false : true,
          },
        },
      }).then(({ data }) => {
        const { active } = data.updateSubscription;

        if (active) {
          return enqueueSnackbar(
            t("subscribeContentButton.subscriptionAdded"),
            {
              variant: "success",
              preventDuplicate: true,
            }
          );
        } else {
          return enqueueSnackbar(
            t("subscribeContentButton.subscriptionRemoved"),
            {
              variant: "success",
              preventDuplicate: true,
            }
          );
        }
      });
    } else {
      document.querySelector(".js-sign-in")?.click();
    }
  };

  const text = active
    ? t("subscribeContentButton.subscribed")
    : t("subscribeContentButton.subscribe");

  const color = "primary";
  const variant = active ? "outlined" : "contained";

  return (
    <Root className={className}>
      <Tooltip
        title={
          active
            ? t("subscribeContentButton.subscribedTitle")
            : t("subscribeContentButton.subscribeTitle")
        }
      >
        <Box component="span" sx={{ width: fullWidth ? "100%" : "auto" }}>
          {size === "small" ? (
            <IconButton
              disabled={loading}
              onClick={onClickSubscribeButton}
              size="small"
              classes={{
                root: classNames(classes.iconButton, {
                  [classes.iconButtonActive]: active,
                }),
              }}
            >
              {active ? (
                <NotificationsActiveIcon fontSize="small" />
              ) : (
                <NotificationsIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Button
              disableElevation
              className={classNames(classes.button, {
                [classes.active]: active,
              })}
              color={color}
              variant={variant}
              disabled={loading}
              fullWidth={fullWidth}
              onClick={onClickSubscribeButton}
              startIcon={
                active ? <NotificationsActiveIcon /> : <NotificationsIcon />
              }
            >
              {text}
            </Button>
          )}
        </Box>
      </Tooltip>
    </Root>
  );
}

SubscribeContentButton.propTypes = {
  size: PropTypes.oneOf(["small", "large"]),
  className: PropTypes.string,
  enqueueSnackbar: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    viewerSubscription: PropTypes.shape({
      notify: PropTypes.bool,
      active: PropTypes.bool,
    }),
  }),
};

export default withSnackbar(SubscribeContentButton);
